import PublicTrackingTokenRepository from "@app-contexts/public/features/tracking-tokens/data/repositories/PublicTrackingTokenRepository";
import { PublicTrackingTokensSerializer } from "@app-contexts/public/features/tracking-tokens/data/types";
import { enums, INVOICE_ITEM_TYPE_OPTIONS } from "@shared/constants";
import { useAnalyticsContext } from "@shared/contexts/AnalyticsContext/useAnalyticsContext";
import { TicketBasketType } from "@shared/modules/basket/types";
import { NestedPublicVenueSerializer } from "@shared/modules/events/types/public";
import { PublicVenueSerializer } from "@app-contexts/public/features/org-profile/data/types/public-venue";
import {
  IHasName,
  IHasTicketTypes,
  IHasAnalyticsParams,
  AnalyticsService,
} from "@shared/services/analytics";
import facebookpixel from "../facebookpixel";
import gtag from "../gtag";
import gtm from "../gtm";
import { triggerTokens } from "../trackingTokens";
import {
  AnalyticEventOptions,
  AnalyticEventTypes,
  GtagTicketType,
  TrackTokenDataType,
} from "../types/analytics-types";

/**
 * A React hook that encapsulates our analytics libraries.
 *
 * @deprecated Use the AnalyticsService class instead
 */
// Allow function types to be inferred
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const useAnalytics = () => {
  const { enableAnalytics } = useAnalyticsContext();
  const trackingTokenRepo = new PublicTrackingTokenRepository();
  /**
   * Initialize a venue's analytics platforms
   */
  const initVenue = (venue: IHasAnalyticsParams) => {
    if (!enableAnalytics) {
      return;
    }

    AnalyticsService.initVenue(venue);
  };

  /**
   * Track a page view on a key page, like an event detail page
   * @see pageView for viewing any page
   */
  const viewContent: AnalyticEventTypes["viewContent"] = (event, opts) => {
    if (!enableAnalytics) {
      return;
    }

    const args = [];
    if (opts) args.push(opts);
    facebookpixel.viewContent(event, ...args);
  };

  /**
   * Track when a ticket type or product list is viewed
   */
  const viewItems: AnalyticEventTypes["viewItems"] = (ttset, venue) => {
    if (!enableAnalytics) {
      return;
    }

    gtag.viewItems(ttset, venue);
    gtm.viewItems(ttset, venue);
  };

  /**
   * Track when any page is viewed, regardless of it's significance
   * @see viewContent for viewing key pages
   */
  const pageView: AnalyticEventTypes["pageView"] = (
    opts?: AnalyticEventOptions
  ) => {
    if (!enableAnalytics) {
      return;
    }

    const args = [];
    if (opts) args.push(opts);
    facebookpixel.pageView(...args);
  };

  /**
   * Track when an item is added to the cart
   */
  const addToCart: AnalyticEventTypes["addToCart"] = (
    group,
    count,
    venue,
    opts
  ) => {
    if (!enableAnalytics) {
      return;
    }

    facebookpixel.addToCart(group, count, venue, opts);
    gtag.addToCart(group, count, venue, opts);
    gtm.addToCart(group, count, venue, opts);
  };

  /**
   * Track when an item is removed from the cart
   */
  const removeFromCart: AnalyticEventTypes["removeFromCart"] = () => {
    if (!enableAnalytics) {
      return;
    }

    AnalyticsService.removeFromCart();
  };

  /**
   * Track when checkout process has been initiated.
   * This should be used when the checkout page is viewed, not when the checkout button is pressed.
   */
  const beginCheckout: AnalyticEventTypes["beginCheckout"] = (
    basket,
    venue,
    opts
  ) => {
    if (!enableAnalytics) {
      return;
    }

    facebookpixel.beginCheckout(basket, venue, opts);
    gtag.beginCheckout(basket, venue);
    gtm.beginCheckout(basket, venue);
  };

  /**
   * Track when a checkout process has been successfully completed
   */
  const postPurchase: AnalyticEventTypes["postPurchase"] = (
    basket,
    venue,
    opts
  ) => {
    if (!enableAnalytics) {
      return;
    }

    facebookpixel.postPurchase(basket, venue, opts);
    gtag.postPurchase(basket, venue);
    gtm.postPurchase(basket, venue);
  };

  /**
   * Adwords only - Track a Google Adwords Conversion event.
   */
  const awConversion: AnalyticEventTypes["awConversion"] = (
    conversionID,
    conversionLabel,
    item,
    ...args
  ) => {
    if (!enableAnalytics) {
      return;
    }

    gtag.awConversion(conversionID, conversionLabel, item, ...args);
    gtm.awConversion(conversionID, conversionLabel, item, ...args);
  };

  /**
   * Trigger tracking tokens that may be available on the venue or event
   */
  const trackToken: AnalyticEventTypes["trackToken"] = (
    location,
    venueID,
    eventIDs,
    data
  ) => {
    if (
      !enableAnalytics ||
      typeof navigator === "undefined" ||
      !navigator?.sendBeacon
    )
      return undefined;

    const requestPromises = [
      // Non-event specific
      trackingTokenRepo.getPublicTrackingTokens({
        location,
        venue: venueID,
        event__in: null,
        event__isnull: true,
      }),
    ];

    // Event specific. These are only included if event__in is valid
    if (eventIDs && eventIDs.length) {
      requestPromises.push(
        trackingTokenRepo.getPublicTrackingTokens({
          location,
          venue: venueID,
          event__in: eventIDs.filter((id) => !!id).join(","),
        })
      );
    }

    return Promise.allSettled(requestPromises)
      .then((results) => {
        const venueWideTokens = results[0];
        const eventSpecificTokens = results[1];

        let allTokens: PublicTrackingTokensSerializer = [];

        if (venueWideTokens?.status === "fulfilled") {
          allTokens = allTokens.concat(venueWideTokens.value);
        }
        if (eventSpecificTokens?.status === "fulfilled") {
          allTokens = allTokens.concat(eventSpecificTokens.value);
        }

        return allTokens;
      })
      .then((allTokens) => {
        return triggerTokens(allTokens, data);
      })
      .catch((err) => {
        // If an error is thrown, catch it and log it without notifying the user
        // eslint-disable-next-line no-console
        console.error(err);
      });
  };

  /**
   * Track any one-off analytics event
   * instances that the other functions could not cover
   */
  const track: AnalyticEventTypes["track"] = (...args) => {
    if (!enableAnalytics) {
      return;
    }

    facebookpixel.track(...args);
    gtag.track(...args);
    gtm.track(...args);
  };

  /**
   * Triggers all post-checkout tracking tokens
   */
  const triggerPostPurchaseTrackingTokens: AnalyticEventTypes["triggerPostPurchaseTrackingTokens"] =
    (basket: TicketBasketType, invoice?) => {
      const totalPrice = basket?.total_price ?? "0.00";
      const basketEventIds =
        basket.item_groups?.map((item) => item.event) ?? [];

      // Build items array from invoice line items
      const items =
        invoice?.invoice_items?.map((item) => ({
          itemId: item.id,
          eventAmount: parseFloat(item.price),
          name: item.description,
          quantity: item.quantity,
          variant: item.ticket_type?.name || item.product?.name || undefined,
        })) ?? [];

      let tokenObj: TrackTokenDataType = {
        fbEvent: "Purchase",
        currency: basket?.currency ?? enums.CURRENCY_MAP.CAD.id,
        venue_id: basket.payment_venue,
        items: items.length > 0 ? items : undefined,
      };

      if (totalPrice) {
        tokenObj = { ...tokenObj, value: parseInt(totalPrice, 10) };
      }
      if (invoice?.transaction_id) {
        tokenObj = { ...tokenObj, transaction_id: invoice.transaction_id };
      }

      trackToken(
        enums.TRACKING_TOKEN_LOCATIONS.LOCATION_POST_CHECKOUT.id,
        basket.payment_venue,
        basketEventIds,
        tokenObj
      );
    };

  /**
   * Tell all analytics platforms that user has consented to analytics
   * TODO consent:  flush out details and update tests
   */
  const grantConsent: AnalyticEventTypes["grantConsent"] = () => {
    if (!enableAnalytics) {
      return;
    }

    facebookpixel.grantConsent();
    gtag.grantConsent();
    gtm.grantConsent();
  };

  /**
   * Tell all analytics platforms that user did not consent to analytics
   * TODO consent:  flush out details and update tests
   */
  const revokeConsent: AnalyticEventTypes["revokeConsent"] = () => {
    if (!enableAnalytics) {
      return;
    }

    facebookpixel.revokeConsent();
    gtag.revokeConsent();
    gtm.revokeConsent();
  };

  /**
   * Formats input ticket_type or product or membership_level data
   * with fields that are expected by the gtag.buildItemList function
   */
  const formatGtagTrackingData: AnalyticEventTypes["formatGtagTrackingData"] = (
    id,
    name,
    variant,
    itemType
  ) => {
    if (!enableAnalytics) {
      return undefined;
    }

    return gtag.formatTrackingData(id, name, variant, itemType);
  };

  const viewTicketTypes = (
    item: IHasTicketTypes & IHasName,
    venue: NestedPublicVenueSerializer | PublicVenueSerializer
  ) => {
    if (
      !enableAnalytics ||
      !item.ticket_types ||
      item.ticket_types.length === 0
    ) {
      return;
    }

    viewItems(
      item.ticket_types?.map((tt) => {
        const trackingData = gtag.formatTrackingData(
          tt.id,
          item.name,
          tt.name,
          INVOICE_ITEM_TYPE_OPTIONS.INVOICE_ITEM_TYPE_TICKET.id
        );
        return {
          ...tt,
          ...trackingData,
        };
      }) as GtagTicketType[],
      venue
    );
  };

  /**
   * Disable a venue's analytics platforms
   */
  const disableVenue = (venue: NestedPublicVenueSerializer) => {
    if (!enableAnalytics) {
      return;
    }

    AnalyticsService.disableVenue(venue);

    if (venue.org_facebook_pixel) {
      facebookpixel.revokeConsent(); // Best approximation for disabling Facebook Pixel
    }

    if (venue.aw_global_tag) {
      gtag.disable(venue.aw_global_tag);
    }

    if (venue.ga_4_tracking_id) {
      gtag.disable(venue.ga_4_tracking_id);
    }

    if (venue.google_tag_manager_id) {
      gtm.disable(venue.google_tag_manager_id);
    }
  };

  /**
   * Disable all active analytics platforms
   */
  const disableAllAnalytics = () => {
    if (!enableAnalytics) {
      return;
    }

    gtag.disableAll();
    facebookpixel.revokeConsent();
  };

  return {
    initVenue,
    disableVenue,
    disableAllAnalytics,
    viewContent,
    viewItems,
    viewTicketTypes,
    pageView,
    addToCart,
    removeFromCart,
    beginCheckout,
    postPurchase,
    awConversion,
    trackToken,
    track,
    grantConsent,
    revokeConsent,
    formatGtagTrackingData,
    triggerPostPurchaseTrackingTokens,
  };
};

export default useAnalytics;
